import { useQueryClient } from '@tanstack/react-query'
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { ACCEPT_FICHIERS, type Depot, RecapDepot, SuiviDepot, useDepot } from './DepotPieces'
import { Icone } from './Icone'

// Glisser une pièce dans une tâche : n'importe où sur la fiche (un voile le confirme), ou sur la
// petite zone sous ses pièces, qui permet aussi de parcourir. Récapitulatif et suivi communs
// (`DepotPieces`) ; envoyée, la pièce rejoint le mail de la tâche, ou la tâche libre elle-même, puis
// elle est lue comme toute pièce déposée.

type DepotDeTache = { depot: Depot; tacheId: string }

const ContexteDepot = createContext<DepotDeTache | null>(null)

/** Un glisser qui porte des fichiers (pas un texte ni un lien de la page). */
const porteDesFichiers = (e: DragEvent) => e.dataTransfer?.types.includes('Files') ?? false

export function DepotTache({
  tacheId,
  actif,
  children,
}: {
  tacheId: string
  /** Faux : ni voile ni zone, et un fichier lâché sur la fiche n'est pas ouvert par le navigateur. */
  actif: boolean
  children: ReactNode
}) {
  const depot = useDepot()
  const [survol, setSurvol] = useState(false)
  // Entrées et sorties se suivent d'un élément à l'autre : on compte pour savoir quand on sort.
  const profondeur = useRef(0)
  // Les écouteurs de la fenêtre appellent toujours le dépôt du dernier rendu.
  const choisirCourant = useRef(depot.choisir)
  choisirCourant.current = depot.choisir

  useEffect(() => {
    const entrer = (e: DragEvent) => {
      if (!porteDesFichiers(e)) return
      e.preventDefault()
      profondeur.current++
      if (actif) setSurvol(true)
    }
    const survoler = (e: DragEvent) => {
      if (!porteDesFichiers(e)) return
      // Toujours empêcher le navigateur d'ouvrir le fichier à la place de la fiche.
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = actif ? 'copy' : 'none'
    }
    const sortir = (e: DragEvent) => {
      if (!porteDesFichiers(e)) return
      profondeur.current = Math.max(0, profondeur.current - 1)
      if (profondeur.current === 0) setSurvol(false)
    }
    const lacher = (e: DragEvent) => {
      if (!porteDesFichiers(e)) return
      e.preventDefault()
      profondeur.current = 0
      setSurvol(false)
      if (actif) choisirCourant.current([...(e.dataTransfer?.files ?? [])])
    }
    window.addEventListener('dragenter', entrer)
    window.addEventListener('dragover', survoler)
    window.addEventListener('dragleave', sortir)
    window.addEventListener('drop', lacher)
    return () => {
      window.removeEventListener('dragenter', entrer)
      window.removeEventListener('dragover', survoler)
      window.removeEventListener('dragleave', sortir)
      window.removeEventListener('drop', lacher)
    }
  }, [actif])

  return (
    <ContexteDepot.Provider value={actif ? { depot, tacheId } : null}>
      {children}
      {actif && survol && (
        <div className="depot-tache-voile" aria-hidden="true">
          <div className="depot-tache-voile-texte">
            <Icone nom="upload_file" taille={28} />
            <strong>Lâcher pour joindre à la tâche</strong>
            <span>PDF ou XML</span>
          </div>
        </div>
      )}
    </ContexteDepot.Provider>
  )
}

/** Vrai quand la fiche accepte une pièce glissée. */
export const useDepotTacheActif = () => useContext(ContexteDepot) !== null

/** La petite zone sous les pièces : glisser ici, ou parcourir. */
export function ZoneDepotTache() {
  const client = useQueryClient()
  const contexte = useContext(ContexteDepot)
  const champ = useRef<HTMLInputElement>(null)
  if (!contexte) return null
  const { depot, tacheId } = contexte
  return (
    <div className="depot-tache">
      <button
        type="button"
        className="depot-tache-zone"
        disabled={depot.enCours}
        onClick={() => champ.current?.click()}
      >
        <Icone nom="upload_file" taille={18} />
        <span className="depot-tache-texte">
          Glisser une pièce ici ou sur la tâche, ou <u>parcourir</u>
        </span>
        <span className="discret">PDF, XML</span>
      </button>
      <input
        ref={champ}
        type="file"
        multiple
        hidden
        accept={ACCEPT_FICHIERS}
        onChange={(e) => {
          depot.choisir([...(e.target.files ?? [])])
          e.target.value = ''
        }}
      />
      <RecapDepot
        depot={depot}
        contexte="Jointe à cette tâche, puis lue comme toute pièce."
        surEnvoyer={() => void depot.envoyer({ tacheId }).then(() => client.invalidateQueries())}
      />
      <SuiviDepot depot={depot} />
    </div>
  )
}
